import React, { useEffect, useMemo, useState } from "react";
import { Alert, Form, Input, List, Modal, Space, Spin, Tag, Typography } from "antd";
import { scheduleDraftDataService } from "../../services/scheduleDraftDataService";
import { formatScheduleDraftError } from "../../lib/errorUtils";

const { Text } = Typography;

function getStatusCount(summary, status) {
    const counts = summary?.counts_by_status || summary?.countsByStatus || {};
    return counts[status] || counts[String(status).toLowerCase()] || summary?.[`${String(status).toLowerCase()}_count`] || 0;
}

function normalizeValidationReady(validation) {
    if (!validation) {
        return false;
    }
    if (typeof validation.ready === "boolean") {
        return validation.ready;
    }
    return Boolean(validation.is_valid);
}

function getValidationIssues(validation) {
    return validation?.issues || validation?.errors || [];
}

function getValidationDiagnostics(validation) {
    return validation?.diagnostics || [];
}

function getIssueFlightLabel(issue) {
    const flight = issue?.flight && typeof issue.flight === "object" ? issue.flight : {};
    const flightNumber = issue?.flight_number || flight.flight_number;
    const departure = issue?.departure_airport || flight.departure_airport;
    const arrival = issue?.arrival_airport || flight.arrival_airport;
    if (flightNumber && departure && arrival) {
        return `${flightNumber} ${departure}-${arrival}`;
    }
    return flightNumber || issue?.flight_id || issue?.affected_flight_ids?.[0] || null;
}

function getIssueTitle(issue) {
    const label = issue?.constraint_label || issue?.code || issue?.constraint_type || "阻断项";
    const flightLabel = getIssueFlightLabel(issue);
    return [label, flightLabel].filter(Boolean).join(" · ");
}

function getIssueDescription(issue) {
    const field = issue?.field_label || issue?.field;
    const business = issue?.business_message;
    const action = issue?.next_action || issue?.suggestion;
    return [
        issue?.message,
        field ? `影响字段：${field}` : null,
        business ? `业务含义：${business}` : null,
        action ? `下一步：${action}` : null,
    ].filter(Boolean).join("；");
}

function getCompareConfirmationExpiresInSeconds(confirmation, nowMs = Date.now()) {
    if (!confirmation?.expires_at) {
        return 0;
    }
    const expiresAtMs = Date.parse(confirmation.expires_at);
    if (!Number.isFinite(expiresAtMs)) {
        return Math.max(Number(confirmation.expires_in_seconds || 0), 0);
    }
    return Math.max(Math.ceil((expiresAtMs - nowMs) / 1000), 0);
}

const FinalizeTrackDialog = ({ open, draft, track, onCancel, onFinalizeTrack, confirmLoading = false }) => {
    const [form] = Form.useForm();
    const [validation, setValidation] = useState(null);
    const [validationError, setValidationError] = useState(null);
    const [compareConfirmation, setCompareConfirmation] = useState(null);
    const [compareConfirmationError, setCompareConfirmationError] = useState(null);
    const [validating, setValidating] = useState(false);
    const [nowTick, setNowTick] = useState(() => Date.now());
    const confirmationText = Form.useWatch("confirmationText", form) || "";
    const confirmationTarget = useMemo(() => String(draft?.name || track?.name || "确认发布").trim(), [draft?.name, track?.name]);
    const validationReady = normalizeValidationReady(validation);
    const issues = getValidationIssues(validation);
    const diagnostics = getValidationDiagnostics(validation);
    const matchSummary = validation?.match_summary || validation?.matchSummary || {};
    const confirmationMatched = confirmationText.trim() === confirmationTarget;
    const compareConfirmationExpiresIn = getCompareConfirmationExpiresInSeconds(compareConfirmation, nowTick);
    const compareConfirmationReady = Boolean(compareConfirmation?.valid_for_publish && String(compareConfirmation?.status || "").toUpperCase() === "CONFIRMED" && compareConfirmationExpiresIn > 0 && !compareConfirmation?.current_issue);

    useEffect(() => {
        if (!open) {
            form.resetFields();
            setValidation(null);
            setValidationError(null);
            setCompareConfirmation(null);
            setCompareConfirmationError(null);
            setValidating(false);
            return;
        }

        if (!draft?.id || !track?.id) {
            return;
        }

        let cancelled = false;
        setValidating(true);
        setValidation(null);
        setValidationError(null);
        setCompareConfirmation(null);
        setCompareConfirmationError(null);
        Promise.allSettled([
            scheduleDraftDataService.validateMatches(draft.id, track.id, track.compared_reference_track_id),
            scheduleDraftDataService.getCompareConfirmation(draft.id, track.id, track.compared_reference_track_id),
        ])
            .then(([validationResult, confirmationResult]) => {
                if (!cancelled) {
                    if (validationResult.status === "fulfilled") {
                        setValidation(validationResult.value);
                    } else {
                        setValidationError(validationResult.reason);
                    }
                    if (confirmationResult.status === "fulfilled") {
                        setCompareConfirmation(confirmationResult.value);
                    } else {
                        setCompareConfirmationError(confirmationResult.reason);
                    }
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setValidating(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [draft?.id, form, open, track?.compared_reference_track_id, track?.id]);

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const timer = window.setInterval(() => setNowTick(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, [open]);

    const handleSubmit = async () => {
        await form.validateFields();
        if (!validationReady || !compareConfirmationReady || !confirmationMatched) {
            return;
        }
        try {
            await onFinalizeTrack?.(track?.id);
            form.resetFields();
            onCancel?.();
        } catch {
            // Mutation error handling is centralized in useScheduleDraftEditorMutations.
        }
    };

    return (
        <Modal
            title="定稿发布"
            open={open}
            onCancel={onCancel}
            onOk={handleSubmit}
            okText="确认发布"
            cancelText="取消"
            confirmLoading={confirmLoading}
            okButtonProps={{ danger: true, disabled: validating || Boolean(validationError) || !validationReady || !compareConfirmationReady || !confirmationMatched }}
            destroyOnClose
        >
            <Space direction="vertical" size={14} style={{ width: "100%" }}>
                <Alert type="warning" showIcon message="发布后轨将变为只读，航班将写入执行层。" />
                <Text>
                    当前轨：<Text strong>{track?.name || track?.id || "未命名轨"}</Text>
                </Text>

                {compareConfirmationError ? (
                    <Alert type="warning" showIcon message="匹配确认状态读取失败" description={formatScheduleDraftError(compareConfirmationError, "无法获取匹配确认状态")} />
                ) : compareConfirmation ? (
                    <Alert
                        type={compareConfirmationReady ? "success" : "warning"}
                        showIcon
                        message={compareConfirmationReady ? `匹配已确认，${compareConfirmationExpiresIn}s 内可发布` : "匹配确认不可用于发布"}
                        description={compareConfirmationReady ? "请在确认有效期内完成发布。" : (compareConfirmation.current_issue || "确认已过期或已被新确认/发布消费，请回到匹配审查重新确认。")}
                    />
                ) : (
                    <Alert type="warning" showIcon message="尚未确认匹配" description="发布前必须在匹配审查中确认匹配，并在 5 分钟内完成发布。" />
                )}

                {validating ? (
                    <Spin tip="正在执行发布前校验..." />
                ) : validationError ? (
                    <Alert type="error" showIcon message="发布前校验失败" description={formatScheduleDraftError(validationError, "无法获取发布前校验结果")} />
                ) : validation ? (
                    <Space direction="vertical" size={10} style={{ width: "100%" }}>
                        <Alert
                            type={validationReady ? "success" : "error"}
                            showIcon
                            message={validationReady ? "发布前校验通过" : "发布前校验未通过"}
                            description={validationReady ? (diagnostics.length ? "匹配完整性与发布硬约束已通过；存在非阻断范围诊断，请确认后发布。" : "匹配完整性、失效引用与硬约束校验均已通过。") : "请修复以下阻断项后再发布。"}
                        />
                        <Space wrap size={[8, 8]}>
                            <Tag color="green">NEW {getStatusCount(matchSummary, "NEW")}</Tag>
                            <Tag color="orange">CANCELLED {getStatusCount(matchSummary, "CANCELLED")}</Tag>
                            <Tag color="blue">MATCHED {getStatusCount(matchSummary, "MATCHED")}</Tag>
                        </Space>
                        {issues.length ? (
                            <Space direction="vertical" size={6} style={{ width: "100%" }}>
                                <List
                                    size="small"
                                    bordered
                                    dataSource={issues.slice(0, 10)}
                                    renderItem={(issue) => (
                                        <List.Item>
                                            <Space direction="vertical" size={2} style={{ width: "100%" }}>
                                                <Space wrap size={[6, 4]}>
                                                    <Tag color="red">{issue?.constraint_type || issue?.code || "BLOCK"}</Tag>
                                                    {issue?.field_label || issue?.field ? <Tag color="orange">{issue.field_label || issue.field}</Tag> : null}
                                                    <Text strong>{getIssueTitle(issue)}</Text>
                                                </Space>
                                                <Text type="secondary">{getIssueDescription(issue) || String(issue)}</Text>
                                            </Space>
                                        </List.Item>
                                    )}
                                />
                                {issues.length > 10 ? <Text type="secondary">另有 {issues.length - 10} 个阻断项未展开，请先处理以上同类问题。</Text> : null}
                            </Space>
                        ) : null}
                        {diagnostics.length ? (
                            <Space direction="vertical" size={6} style={{ width: "100%" }}>
                                <List
                                    size="small"
                                    bordered
                                    dataSource={diagnostics.slice(0, 10)}
                                    renderItem={(diagnostic) => (
                                        <List.Item>
                                            <Space direction="vertical" size={2} style={{ width: "100%" }}>
                                                <Space wrap size={[6, 4]}>
                                                    <Tag color="gold">{diagnostic?.constraint_type || diagnostic?.code || "诊断"}</Tag>
                                                    {diagnostic?.field_label || diagnostic?.field ? <Tag color="orange">{diagnostic.field_label || diagnostic.field}</Tag> : null}
                                                    <Text strong>{getIssueTitle(diagnostic)}</Text>
                                                </Space>
                                                <Text type="secondary">{getIssueDescription(diagnostic) || String(diagnostic)}</Text>
                                            </Space>
                                        </List.Item>
                                    )}
                                />
                                {diagnostics.length > 10 ? <Text type="secondary">另有 {diagnostics.length - 10} 个非阻断诊断未展开。</Text> : null}
                            </Space>
                        ) : null}
                    </Space>
                ) : null}

                <Form form={form} layout="vertical">
                    <Form.Item
                        label={`输入航班计划名称“${confirmationTarget}”以确认发布`}
                        name="confirmationText"
                        rules={[{ required: true, message: "请输入航班计划名称确认发布" }]}
                    >
                        <Input placeholder={confirmationTarget} autoComplete="off" />
                    </Form.Item>
                </Form>
            </Space>
        </Modal>
    );
};

export default FinalizeTrackDialog;

export { getIssueDescription, getIssueFlightLabel, getIssueTitle };
